/**
 * Hierarchy Map Overlay Component
 *
 * Renders sector boundaries on a Leaflet map with color coding,
 * status indicators, and click handling for sector selection.
 */

import { useCallback } from 'react'
import { Circle, Marker, Popup } from 'react-leaflet'
import L from 'leaflet'
import type { GeoLocation, HierarchyRoomType } from '@/lib/types'
import type { SectorMapOverlay } from './hierarchyMapUtils'

// Re-export types for backwards compatibility
export type { SectorMapOverlay } from './hierarchyMapUtils'

// Sector colors for distinct visual identification
const SECTOR_COLORS = [
  '#8b5cf6', // purple
  '#06b6d4', // cyan
  '#f59e0b', // amber
  '#10b981', // emerald
  '#ec4899', // pink
  '#6366f1', // indigo
  '#14b8a6', // teal
  '#f97316', // orange
]

interface HierarchyMapOverlayProps {
  hierarchyType: HierarchyRoomType
  commandBoundary?: {
    center: GeoLocation
    radiusMeters: number
  }
  sectors: SectorMapOverlay[]
  showCommandBoundary?: boolean
  showSectorCentroids?: boolean
  showParticipantCounts?: boolean
  selectedSectorId?: string
  onSectorClick?: (sectorRoomId: string) => void
  className?: string
}

export function HierarchyMapOverlay({
  hierarchyType,
  commandBoundary,
  sectors,
  showCommandBoundary = true,
  showSectorCentroids = true,
  showParticipantCounts = true,
  selectedSectorId,
  onSectorClick,
}: HierarchyMapOverlayProps) {
  // Assign colors to sectors
  const getSectorColor = useCallback((index: number, sector: SectorMapOverlay) => {
    if (sector.color) return sector.color
    return SECTOR_COLORS[index % SECTOR_COLORS.length]
  }, [])

  // Get status-based opacity and styling
  const getStatusStyle = useCallback((status: string) => {
    switch (status) {
      case 'active':
        return { fillOpacity: 0.15, opacity: 1, dashArray: undefined }
      case 'alert':
        return { fillOpacity: 0.25, opacity: 1, dashArray: '10, 5' }
      case 'moving':
        return { fillOpacity: 0.1, opacity: 0.8, dashArray: '5, 10' }
      case 'offline':
        return { fillOpacity: 0.05, opacity: 0.4, dashArray: '2, 8' }
      default:
        return { fillOpacity: 0.1, opacity: 0.8, dashArray: undefined }
    }
  }, [])

  // Handle sector click
  const handleSectorClick = useCallback(
    (sectorRoomId: string) => {
      if (onSectorClick) {
        onSectorClick(sectorRoomId)
      }
    },
    [onSectorClick]
  )

  return (
    <>
      {/* Command boundary (if showing command view) */}
      {showCommandBoundary && commandBoundary && hierarchyType === 'command' && (
        <>
          <Circle
            center={[commandBoundary.center.latitude, commandBoundary.center.longitude]}
            radius={commandBoundary.radiusMeters}
            pathOptions={{
              color: '#f20d80',
              fillColor: '#f20d80',
              fillOpacity: 0.05,
              weight: 2,
              dashArray: '10, 5',
            }}
          />
          {/* Command center marker */}
          <Marker
            position={[commandBoundary.center.latitude, commandBoundary.center.longitude]}
            icon={createCommandCenterIcon()}
          >
            <Popup>
              <div className="text-xs font-mono">
                <div className="font-bold text-primary uppercase tracking-wider mb-1">Command Center</div>
                <div className="text-gray-600">Radius: {commandBoundary.radiusMeters}m</div>
                <div className="text-gray-600">Sectors: {sectors.length}</div>
              </div>
            </Popup>
          </Marker>
        </>
      )}

      {/* Sector boundaries */}
      {sectors.map((sector, index) => {
        const color = getSectorColor(index, sector) ?? '#8b5cf6'
        const statusStyle = getStatusStyle(sector.status)
        const isSelected = selectedSectorId === sector.roomId

        return (
          <g key={sector.roomId}>
            {/* Sector boundary circle */}
            <Circle
              center={[sector.center.latitude, sector.center.longitude]}
              radius={sector.radiusMeters}
              pathOptions={{
                color: isSelected ? '#ffffff' : color,
                fillColor: color,
                fillOpacity: isSelected ? 0.3 : statusStyle.fillOpacity,
                weight: isSelected ? 3 : 2,
                opacity: statusStyle.opacity,
                dashArray: statusStyle.dashArray,
              }}
              eventHandlers={{
                click: () => handleSectorClick(sector.roomId),
              }}
            >
              <Popup>
                <SectorPopup sector={sector} color={color} />
              </Popup>
            </Circle>

            {/* Sector center/status marker */}
            <Marker
              position={[sector.center.latitude, sector.center.longitude]}
              icon={createSectorIcon(sector, color, showParticipantCounts)}
              eventHandlers={{
                click: () => handleSectorClick(sector.roomId),
              }}
            >
              <Popup>
                <SectorPopup sector={sector} color={color} />
              </Popup>
            </Marker>

            {/* Participant centroid (if different from center) */}
            {showSectorCentroids && sector.participantCentroid && (
              <Marker
                position={[sector.participantCentroid.latitude, sector.participantCentroid.longitude]}
                icon={createCentroidIcon(color)}
              >
                <Popup>
                  <div className="text-xs font-mono">
                    <div className="font-bold uppercase tracking-wider" style={{ color }}>
                      {sector.name} - Participant Centroid
                    </div>
                    <div className="text-gray-600 mt-1">
                      Calculated center of {sector.participantCount} participants
                    </div>
                  </div>
                </Popup>
              </Marker>
            )}
          </g>
        )
      })}
    </>
  )
}

// ============= Sub-components =============

function SectorPopup({ sector, color }: { sector: SectorMapOverlay; color: string }) {
  const statusColors = {
    active: '#22c55e',
    alert: '#f97316',
    moving: '#3b82f6',
    offline: '#6b7280',
  }

  return (
    <div className="text-xs min-w-[140px] font-mono">
      <div
        className="font-bold uppercase tracking-wider pb-1 mb-2 border-b-2"
        style={{ color, borderColor: color }}
      >
        {sector.name}
      </div>
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: statusColors[sector.status] }}
          />
          <span className="capitalize text-gray-700">{sector.status}</span>
        </div>
        <div className="text-gray-600">
          <span className="text-gray-500">Participants:</span> {sector.participantCount}
        </div>
        <div className="text-gray-600">
          <span className="text-gray-500">Radius:</span> {sector.radiusMeters}m
        </div>
      </div>
    </div>
  )
}

// ============= Icon factories =============

function createCommandCenterIcon() {
  return L.divIcon({
    className: 'command-center-marker',
    html: `
      <div style="
        width: 24px;
        height: 24px;
        background: #f20d80;
        border: 3px solid white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        box-shadow: 0 0 15px rgba(242, 13, 128, 0.6);
      ">
        <span style="color: white; font-weight: bold;">C</span>
      </div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  })
}

function createSectorIcon(
  sector: SectorMapOverlay,
  color: string,
  showCount: boolean
) {
  const statusIndicator = getStatusIndicator(sector.status)

  return L.divIcon({
    className: 'sector-marker',
    html: `
      <div style="
        position: relative;
        width: 28px;
        height: 28px;
        background: ${color};
        border: 2px solid white;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 10px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        color: white;
        font-weight: bold;
      ">
        ${showCount ? sector.participantCount : ''}
        <div style="
          position: absolute;
          top: -4px;
          right: -4px;
          width: 10px;
          height: 10px;
          background: ${statusIndicator.color};
          border: 1.5px solid white;
          border-radius: 50%;
          ${statusIndicator.animate ? 'animation: pulse 2s infinite;' : ''}
        "></div>
      </div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  })
}

function createCentroidIcon(color: string) {
  return L.divIcon({
    className: 'centroid-marker',
    html: `
      <div style="
        width: 10px;
        height: 10px;
        background: ${color};
        border: 2px solid white;
        border-radius: 50%;
        opacity: 0.7;
        box-shadow: 0 0 8px ${color};
      "></div>
    `,
    iconSize: [10, 10],
    iconAnchor: [5, 5],
  })
}

function getStatusIndicator(status: string): { color: string; animate: boolean } {
  switch (status) {
    case 'active':
      return { color: '#22c55e', animate: false }
    case 'alert':
      return { color: '#f97316', animate: true }
    case 'moving':
      return { color: '#3b82f6', animate: true }
    case 'offline':
      return { color: '#6b7280', animate: false }
    default:
      return { color: '#6b7280', animate: false }
  }
}

export default HierarchyMapOverlay
